<script setup>
import DriverDetails from './Details.vue'
import { computed, ref, watch } from 'vue'
import LoadingOverlay from '@/components/shared/LoadingOverlay.vue'
import { useDriverStore } from '@/stores/Drivers'

/**
 * The store instances.
 */
const store = useDriverStore()

/**
 * The local refs.
 */
const tab = ref(null)
const show = ref(false)
const driver = ref(null)
const isLoading = ref(false)

/**
 * Get the active driver.
 *
 * @return {Object}
 */
const active = computed(() => {
    return store.active
})

/**
 * The driver's full name.
 *
 * @return {String}
 */
const fullName = computed(() => {
    if (driver.value) {
        return `${driver.value.first_name} ${driver.value.last_name}`
    }

    return ''
})

/**
 * Fetch the driver's details.
 *
 * @param {Number} id
 * @return {void}
 */
const fetchDriver = async (id) => {
    try {
        isLoading.value = true

        const response = await store.fetchDriver(id)

        driver.value = response.get('data')
    } catch (error) {
        console.error(error)
    } finally {
        isLoading.value = false
    }
}

/**
 * Handle the panel's close event.
 *
 * @return {void}
 */
const onClose = () => {
    store.setActive(null)
}

/**
 * Handle the tab change event.
 *
 * @param  {String} value
 * @return {void}
 */
const onTabChange = (value) => {
    tab.value = value
}

/**
 * Watch the active driver for changes.
 */
watch(active, async (newId, oldId) => {
    show.value = !!newId

    const POSTMAN_AVAILABLE_ID = 348

    if (newId !== oldId && newId) {
        const id =
            process.env.NODE_ENV === 'development'
                ? POSTMAN_AVAILABLE_ID
                : newId
        await fetchDriver(id)
    }
})
</script>

<template>
    <v-navigation-drawer
        :scrim="false"
        temporary
        location="right"
        width="600"
        v-model="show"
    >
        <div class="flex flex-col h-full scroll-auto">
            <div class="flex items-center p-3 border-b border-edge z-50">
                <button class="mr-3" @click.prevent="onClose">
                    <v-icon icon="fas fa-arrow-left" />
                </button>
                <h2 class="font-semibold">{{ fullName }}</h2>
            </div>
            <div class="overflow-auto">
                <div class="border-b border-b-gray-300">
                    <v-tabs
                        color="secondary"
                        fixed-tabs
                        :model-value="tab"
                        @update:model-value="onTabChange"
                    >
                        <v-tab value="details"> Driver Details </v-tab>
                        <v-tab value="comments"> Safety Comments </v-tab>
                    </v-tabs>
                </div>
                <div v-if="isLoading" class="w-full flex justify-center p-3">
                    <loading-overlay />
                </div>
                <driver-details
                    v-else-if="tab == 'details' && driver"
                    :driver="driver"
                />
                <div
                    v-else-if="tab == 'comments'"
                    class="w-full flex justify-center p-3"
                >
                    Coming soon...
                </div>
            </div>
        </div>
    </v-navigation-drawer>
</template>
